<div class="message-wrapper" [attr.data-message-id]="conversationMessage?.messageId">
    <!-- Accuris Logo (matching assistant-message) -->
    <span class="accuris-logo"></span>

    <!-- Message Content Container -->
    <div class="message-content">
        <!-- Cognitive Tree Section (always mounted when enabled) -->
        <div class="cognitive-tree" *ngIf="hasChainOfThoughtContent" [class.collapsed]="isChainOfThoughtCollapsed">
            <!-- Collapsed View: Show current step only -->
            <div class="cognitive-tree-collapsed" *ngIf="isChainOfThoughtCollapsed" (click)="toggleChainOfThought()">
                <div class="current-step-header">
                    <span class="collapse-icon">▶</span>
                    <span class="current-step-title">
                        <ng-container *ngIf="isStreaming && currentActiveEventSummary; else staticTitle">
                            <span class="loading-indicator"></span>
                            <span class="shimmer-text">{{ currentActiveEventSummary }}</span>
                        </ng-container>
                        <ng-template #staticTitle>
                            <span *ngIf="isStreaming">
                                <span class="loading-indicator"></span>
                                <span class="shimmer-text connect-to-agent">Connecting to agent...</span>
                            </span>
                            <span *ngIf="!isStreaming">Chain of Thought</span>
                        </ng-template>
                    </span>
                    <span class="event-counter" *ngIf="eventCount > 0">({{ eventCount }} events)</span>
                </div>
            </div>

            <!-- Expanded View: Show full tree -->
            <div class="cognitive-tree-expanded" *ngIf="!isChainOfThoughtCollapsed">
                <div class="cognitive-tree-header" (click)="toggleChainOfThought()">
                    <span class="collapse-icon">▼</span>
                    <h3 id="cot">Chain of Thought</h3>
                    <span class="event-counter" *ngIf="eventCount > 0">({{ eventCount }} events)</span>
                </div>

                <div class="cognitive-tree-container">
                    <!-- CHRONOLOGICALLY GROUPED view: Events in true time order but logically grouped -->
                    <div class="chronological-grouped-container">
                        <ng-container *ngFor="let group of chronologicalEventGroups; trackBy: trackByEventGroup">
                            <div class="event-group" [ngClass]="'group-' + group.groupType">
                                <!-- Group header (optional - can be hidden for cleaner look) -->
                                <div class="group-header" *ngIf="group.title" style="display: none">
                                    <span class="group-title">{{ group.title }}</span>
                                    <span class="group-event-count">({{ group.events.length }} events)</span>
                                </div>

                                <!-- Events in chronological order within this group -->
                                <div class="group-events">
                                    <div
                                        *ngFor="let eventWithContext of group.events; trackBy: trackByEventWithContext"
                                        class="chronological-event"
                                        [ngClass]="'event-from-' + eventWithContext.span.span.span_kind"
                                    >
                                        <craig-streaming-icon
                                            class="event-icon"
                                            [name]="getEventIcon(eventWithContext.event)"
                                            [alt]="eventWithContext.event.event_type"
                                        ></craig-streaming-icon>
                                        <span
                                            class="event-content dark-text"
                                            [ngClass]="{
                                                'streaming-shimmer': shouldShowShimmer(eventWithContext.event, null)
                                            }"
                                            >{{ filterNodeText(eventWithContext.event.delta) }}</span
                                        >
                                        <span
                                            class="loading-indicator"
                                            *ngIf="shouldShowShimmer(eventWithContext.event, null)"
                                        ></span>
                                    </div>
                                </div>
                            </div>
                        </ng-container>

                        <!-- Show message if no events available -->
                        <div *ngIf="chronologicalEventGroups.length === 0" class="no-events-message">
                            <span *ngIf="isStreaming" class="processing-message">
                                <span class="loading-indicator"></span>
                                <span class="shimmer-text connect-to-agent">Connecting to agent...</span>
                            </span>
                            <span *ngIf="!isStreaming">No cognitive events recorded</span>
                        </div>
                    </div>
                </div>
            </div>
        </div>

        <!-- Response Content Section -->
        <div class="response-content">
            <div class="response-text" *ngIf="displayContent && displayContent.length > 0">
                <!-- Use markdown component if response contains markdown, otherwise plain text -->
                <markdown class="body-md-16-regular" [data]="displayContent"> </markdown>
                <!-- Removed "Thinking..." - now using flashing cursor on active events -->
            </div>

            <!-- Datasheet Links Section -->
            <!-- IMPORTANT: Keep datasheets outside markdown content so they always render at the end -->
            <div class="datasheets-section" *ngIf="hasDatasheetSection">
                <div class="datasheets-status" *ngIf="datasheetState?.status === 'loading'">
                    Retrieving datasheet documents
                </div>

                <div class="datasheets-list" *ngIf="datasheetState?.status === 'found'">
                    <div class="datasheets-title">Access the latest datasheet documents</div>

                    <a
                        *ngFor="let sheet of datasheetLinks; let i = index"
                        class="datasheet-link"
                        [href]="getDatasheetUrl(sheet.safeUrl)"
                        target="_blank"
                        rel="noopener noreferrer"
                        (click)="onDatasheetLinkClick(sheet, i)"
                    >
                        {{ sheet.manufacturerName }}
                        <span *ngIf="sheet.title"> — {{ sheet.title }}</span>
                    </a>
                </div>

                <div class="datasheets-empty" *ngIf="datasheetState?.status === 'none'">
                    No datasheet documents available
                </div>

                <div class="datasheets-empty" *ngIf="datasheetState?.status === 'unavailable'">
                    Datasheet documents unavailable right now
                </div>
            </div>

            <!-- Message metadata (optional, shown only when relevant) -->
            <!-- <div class="message-meta" *ngIf="isHistorical || conversationMessage"> -->
            <!-- <span *ngIf="isHistorical">Historical message</span> -->
            <!-- <span *ngIf="!isHistorical">Streaming response</span> -->
            <!-- <span *ngIf="cognitiveTree.length > 0"> • {{ cognitiveTree.length }} Steps</span> -->
            <!-- <span *ngIf="responseLength > 0"> • {{ responseLength }} chars</span> -->
            <!-- </div> -->
        </div>

        <!-- Stats Section (Hidden by default, uncomment if needed) -->
        <!-- <div class="stats show" id="stats">
            <div class="stat-item">
                <div class="stat-value">{{ totalEvents }}</div>
                <div class="stat-label">Total Events</div>
            </div>
            <div class="stat-item">
                <div class="stat-value">{{ totalTime }}ms</div>
                <div class="stat-label">Total Time</div>
            </div>
            <div class="stat-item">
                <div class="stat-value">{{ nodesExecuted }}</div>
                <div class="stat-label">Nodes</div>
            </div>
            <div class="stat-item">
                <div class="stat-value">{{ toolsExecuted }}</div>
                <div class="stat-label">Tools</div>
            </div>
            <div class="stat-item">
                <div class="stat-value">{{ responseLength }}</div>
                <div class="stat-label">Response Chars</div>
            </div>
        </div> -->

        <!-- Filters Section (Hidden by default, uncomment if needed) -->
        <!-- <div class="filters show">
            <label>Filter by Domain:</label>
            <select [(ngModel)]="domainFilter" (ngModelChange)="onFilterChange()">
                <option value="all">All Domains</option>
                <option value="signal">Signal (Input Processing)</option>
                <option value="insight">Insight (Data Analysis)</option>
                <option value="executive">Executive (Conversation)</option>
            </select>

            <label>Show only slow operations (>1000ms):</label>
            <input type="checkbox" [(ngModel)]="showSlowOpsOnly" (ngModelChange)="onFilterChange()" />

            <label>Auto-scroll:</label>
            <input type="checkbox" [(ngModel)]="autoScroll" />
        </div> -->
    </div>
</div>
